import { useState } from "react";
import { Mail, X, Loader2 } from "lucide-react";
import { Button } from "@repo/ui";
import { resendVerification } from "@/lib/api/auth";

interface EmailVerificationBannerProps {
  email: string;
}

const DISMISS_KEY = "c2v-email-banner-dismissed";

export function EmailVerificationBanner({
  email,
}: EmailVerificationBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  });
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  if (dismissed) return null;

  const handleResend = async () => {
    setIsResending(true);
    setResendSuccess(false);

    try {
      await resendVerification({ email });
      setResendSuccess(true);
    } catch {
      // Silently fail — user can retry
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Mail className="h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            {resendSuccess
              ? "Verification email sent! Check your inbox."
              : "Please verify your email address. Check your inbox for the verification link."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!resendSuccess && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResend}
              disabled={isResending}
              className="h-7 border-amber-300 bg-white text-xs text-amber-800 hover:bg-amber-100"
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend Email"
              )}
            </Button>
          )}
          <button
            onClick={handleDismiss}
            className="rounded p-1 text-amber-600 hover:bg-amber-100 hover:text-amber-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
